import React from 'react';
import { createRoot } from 'react-dom/client'
import './index.css';
import App from './App';
import * as serviceWorker from './serviceWorker';
import { installConsoleCapture } from './services/errorReport';
import { isPwaInstallUiEnabled } from './services/platform/adapters/pwa';
import { initBackgroundLocationChallengeListener } from './services/platform/adapters/location';

// Record console output from the very first line, so a crash later on can be
// reported with the messages that preceded it (the error dialog copies them).
installConsoleCapture()

// #1696 (spec.md §8.2-§8.5): arm the Background Location Challenge silent-push
// listener at startup, not on login — a challenge push can arrive whenever the
// native app process is alive. No-op on Web (see the function's own comment).
initBackgroundLocationChallengeListener()

// Capture the install prompt ASAP — before React mounts — so it is never missed.
// The hook reads window.__pwaInstallPrompt if the event already fired.
// #1690 (spec addendum §5.2/§9.3): a native build is already installed from a
// store — the PWA install prompt is not shown inside the wrapper.
if (isPwaInstallUiEnabled()) {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    window.__pwaInstallPrompt = e
    window.dispatchEvent(new Event('pwaInstallPromptReady'))
  })
}

document.documentElement.setAttribute('dir', 'rtl')
document.documentElement.setAttribute('lang', 'he')
document.body.setAttribute('dir', 'rtl')

// Force theme-color to stay blue — MUI CssBaseline sets color-scheme:light
// which causes Chrome to reset the status bar to white.
const THEME_COLOR = '#1976d2'
const themeColorMeta = document.querySelector('meta[name="theme-color"]')
if (themeColorMeta) themeColorMeta.setAttribute('content', THEME_COLOR)

//ReactDOM.render(<App />, document.getElementById('root'));
const root = createRoot(document.getElementById('root'))
root.render(<App/>);

// #192 + stale-cache fix: register the canonical /service-worker.js. It must
// live at THIS path so it replaces any old CRA/Workbox precaching SW still
// registered on clients (that stale SW is what kept serving an old build — see
// public/service-worker.js). The new SW caches nothing and purges old caches,
// so content stays fresh while "add to home screen" (installability) works.
//
// Do NOT reload on `controllerchange`: combined with the SW reloading clients on
// activate, it caused a ~30s refresh loop on entry (reported during a live demo).
// The SW does network passthrough once it takes control, so content is fresh on
// the next navigation without any forced reload.
// #1690 (spec addendum §5.2/§9.3): this service worker exists for PWA
// installability/cache-freshness in a regular browser; a native build updates
// through the app store instead, so it is not registered inside the wrapper.
if (isPwaInstallUiEnabled() && 'serviceWorker' in navigator && window.location.protocol === 'https:') {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register(`${process.env.PUBLIC_URL || ''}/service-worker.js`)
      .catch((err) => console.warn('service worker registration failed', err));
  });
} else {
  serviceWorker.unregister();
}
